import { useKeycloak } from "@react-keycloak/web";
import {
  createContext,
  ReactNode,
  useEffect,
  useContext,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from "react";

import { useSubscription } from "store/auth/auth-store";

import { isCurrentSubscriptionFrom } from "helpers/accountHelper";
import { IconsBySubscription } from "helpers/SubscriptionIconHelper";
import { RolesEnum } from "utils/@types";

interface ISidebarContext {
  collapsed: boolean;
  collapsedDocumentation: boolean;
  toggled: boolean;
  broken: boolean;
  rtl: boolean;
  hasImage: boolean;
  theme: Theme;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  setCollapsedDocumentation: Dispatch<SetStateAction<boolean>>;
  setToggled: Dispatch<SetStateAction<boolean>>;
  setBroken: Dispatch<SetStateAction<boolean>>;
  setRtl: Dispatch<SetStateAction<boolean>>;
  setHasImage: Dispatch<SetStateAction<boolean>>;
  setTheme: Dispatch<SetStateAction<Theme>>;
  routes: any;
  subscriptionName: any;
  avatar: any;
  setRoutes: Dispatch<SetStateAction<any>>;
  isCreateReleaseNotes: boolean;
  isDocumentation: boolean;
  isNewHeaderUVerify: boolean;
}

type Theme = "light" | "dark";

interface ISidebarProviderProps {
  children: ReactNode;
}

const SidebarContext = createContext<ISidebarContext>({} as ISidebarContext);

export function SidebarProvider({ children }: ISidebarProviderProps) {
  const { subscription, subscriptionsAvailable } = useSubscription();

  const { keycloak } = useKeycloak();

  const [subscriptionName, setSubscriptioName] = useState("");
  const [avatar, setAvatar] = useState(null);

  const [routes, setRoutes] = useState([]);

  const [collapsed, setCollapsed] = useState(false);
  const [collapsedDocumentation, setCollapsedDocumentation] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [broken, setBroken] = useState(false);
  const [rtl, setRtl] = useState(false);
  const [hasImage, setHasImage] = useState(false);
  const [theme, setTheme] = useState<Theme>("dark");

  const isCreateReleaseNotes = keycloak?.hasRealmRole(
    RolesEnum.CREATE_RELEASE_NOTES,
  );

  const isDocumentation = keycloak?.hasRealmRole(RolesEnum.DOCUMENTATION);

  const isNewHeaderUVerify = keycloak?.hasRealmRole(
    RolesEnum.NEW_HEADER_UVERIFY,
  );

  useEffect(() => {
    routes.forEach((route) => {
      if (route.feature) {
        route.invisible = !keycloak.hasResourceRole(route.feature, "uverify");
      }
    });
  }, [routes]);

  useEffect(() => {
    const favicon = document.getElementById("favicon");

    if (
      !isCurrentSubscriptionFrom(
        "ECI",
        subscription?.subscriptionId || "",
        subscriptionsAvailable,
      )
    ) {
      document.title = "uVerify";
      favicon.setAttribute("href", "/favicon.ico");
    } else {
      document.title = "Gestión de Externos";
      favicon.setAttribute("href", "/elcorteingles.ico");
    }
  }, [subscription]);

  useEffect(() => {
    if (subscription) {
      setSubscriptioName(subscription.name);
      setAvatar(IconsBySubscription[subscription.mini]);
    }
  }, [subscription]);

  const value = useMemo(
    () => ({
      collapsed,
      collapsedDocumentation,
      toggled,
      broken,
      rtl,
      hasImage,
      theme,
      setCollapsed,
      setCollapsedDocumentation,
      setToggled,
      setBroken,
      setRtl,
      setHasImage,
      setTheme,
      routes,
      avatar,
      subscriptionName,
      setRoutes,
      isCreateReleaseNotes,
      isDocumentation,
      isNewHeaderUVerify,
    }),
    [
      collapsed,
      collapsedDocumentation,
      toggled,
      broken,
      rtl,
      hasImage,
      theme,
      setCollapsed,
      setCollapsedDocumentation,
      setToggled,
      setBroken,
      setRtl,
      setHasImage,
      setTheme,
      routes,
      avatar,
      subscriptionName,
      setRoutes,
      isCreateReleaseNotes,
      isDocumentation,
      isNewHeaderUVerify,
    ],
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSideBar(): ISidebarContext {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error("useSideBar must be used within an SidebarProvider");
  }

  return context;
}
